import { Component, OnInit, Renderer2, Inject, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SharedModule } from '@shared/shared-module';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@shared/services/auth.service';
import { UserPermissionsService } from '@shared/services/user-permissions.service';
import { NotificationService } from '@shared/services/notification.service';
import { Login, LoginResponse } from '@shared/models';
import { BehaviorSubject } from 'rxjs';
import { TenantService } from '@core/tenant/tenant.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: true,
    imports: [SharedModule, ReactiveFormsModule, CommonModule]
})
export class LoginComponent implements OnInit {
    private readonly tenantService = inject(TenantService);

    loginForm!: FormGroup;
    loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    isDarkMode = false;

    get currentLogo(): string {
        return this.tenantService.getLogo(this.isDarkMode);
    }

    get currentTenantName(): string {
        return this.tenantService.tenantName();
    }

    constructor(
        private fb: FormBuilder,
        private router: Router,
        public translate: TranslateService,
        private authService: AuthService,
        private userPermissionsService: UserPermissionsService,
        private notificationService: NotificationService,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document
    ) { }

    toggleLanguage() {
        const currentLang = this.translate.currentLang || 'pt';
        const newLang = currentLang === 'en' ? 'pt' : 'en';
        this.translate.use(newLang);
    }

    ngOnInit(): void {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'dark') {
            this.isDarkMode = true;
            this.renderer.addClass(this.document.body, 'dark_mode');
        } else {
            this.renderer.removeClass(this.document.body, 'dark_mode');
        }

        this.tenantService.applyPalette(this.isDarkMode);

        this.loginForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, Validators.minLength(6)]]
        });
    }

    toggleTheme() {
        this.isDarkMode = !this.isDarkMode;
        if (this.isDarkMode) {
            this.renderer.addClass(this.document.body, 'dark_mode');
            localStorage.setItem('theme', 'dark');
        } else {
            this.renderer.removeClass(this.document.body, 'dark_mode');
            localStorage.setItem('theme', 'light');
        }

        this.tenantService.applyPalette(this.isDarkMode);
    }

    onSubmit(): void {
        if (this.loginForm.valid) {
            this.loading.next(true);
            const credentials: Login = {
                username: this.loginForm.value.email,
                password: this.loginForm.value.password
            };

            this.authService.login(credentials).subscribe({
                next: (res: LoginResponse) => {
                    const token = res.access_token;
                    if (!token) {
                        this.loading.next(false);
                        return;
                    }
                    this.authService.startSession(res);
                    this.notificationService.startConnection(this.tenantService.getCurrentTenantId());
                    this.notificationService.loadAll();
                    this.userPermissionsService.load().subscribe({
                        next: () => {
                            this.loading.next(false);
                            this.router.navigate(['/home']);
                        },
                        error: () => {
                            this.loading.next(false);
                            this.router.navigate(['/home']);
                        }
                    });
                },
                error: (err: any) => {
                    this.loading.next(false);
                    console.error(err);
                }
            });
        } else {
            this.loginForm.markAllAsTouched();
        }
    }
}
